import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowRight, CheckCircle2, MessageCircle, Mail, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";
import { BRAND, CONTACT } from "@/lib/content";
import { LogoLockup } from "./LogoLockup";

// The subscribe button surfaces three visual states. Extract to keep the JSX
// scannable and eliminate a nested ternary.
const renderSubscribeButtonLabel = ({ loading, subscribed, defaultLabel }) => {
  if (loading) return "…";
  if (subscribed) return <CheckCircle2 className="h-4 w-4" />;
  return defaultLabel;
};

export const Footer = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error(t("footer.enterEmailToast"));
      return;
    }
    setLoading(true);
    try {
      const res = await subscribeNewsletter({ email, source: "footer" });
      setSubscribed(true);
      toast.success(res.message || t("footer.subscribed"));
      setEmail("");
    } catch (err) {
      toast.error(t("footer.couldNotSubscribe"));
    } finally {
      setLoading(false);
    }
  };

  const linkCls = "text-sm text-slate-600 hover:text-slate-900";

  return (
    <footer data-testid="site-footer" className="border-t border-slate-200 bg-white">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <LogoLockup size="lg" />
            <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-600">{t("footer.blurb")}</p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
              <ShieldCheck className="h-4 w-4" /> {t("brand.complianceLine")}
            </div>

            <div className="mt-6 space-y-2 text-sm text-slate-600">
              <a className="flex items-start gap-2 hover:text-slate-900" href={`mailto:${CONTACT.emailHelp}`} data-testid="footer-email-help">
                <Mail className="mt-0.5 h-4 w-4 text-slate-400" /> {CONTACT.emailHelp}
              </a>
              <a className="flex items-start gap-2 hover:text-slate-900" href={`mailto:${CONTACT.emailCollab}`} data-testid="footer-email-collab">
                <Mail className="mt-0.5 h-4 w-4 text-slate-400" /> {CONTACT.emailCollab}
              </a>
              <a className="flex items-start gap-2 hover:text-slate-900" href={`https://wa.me/${CONTACT.whatsappE164}`} target="_blank" rel="noreferrer" data-testid="footer-whatsapp">
                <MessageCircle className="mt-0.5 h-4 w-4 text-slate-400" /> WhatsApp: {CONTACT.whatsapp}
              </a>
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 text-slate-400" />
                <span>
                  {CONTACT.addressLine1}<br />
                  {CONTACT.addressLine2}<br />
                  {CONTACT.addressLine3}
                </span>
              </div>
            </div>

            <form onSubmit={handleSubscribe} className="mt-7 max-w-md" data-testid="footer-newsletter-form">
              <label htmlFor="newsletter-email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {t("footer.institutionalUpdates")}
              </label>
              <div className="mt-2 flex gap-2">
                <Input id="newsletter-email" data-testid="footer-newsletter-email-input" type="email" required
                  placeholder={t("footer.placeholderEmail")} value={email} onChange={(e) => setEmail(e.target.value)}
                  className="h-11 rounded-lg border-slate-200 focus-visible:ring-blue-600" />
                <Button type="submit" data-testid="footer-newsletter-submit-button" disabled={loading}
                  className="h-11 rounded-lg bg-slate-900 px-4 text-white hover:bg-slate-800">
                  {renderSubscribeButtonLabel({ loading, subscribed, defaultLabel: t("footer.subscribe") })}
                </Button>
              </div>
              <p className="mt-2 text-xs text-slate-500">{t("footer.newsletterHelp")}</p>
            </form>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("footer.solutions")}</h4>
            <ul className="mt-4 space-y-2.5">
              <li><Link className={linkCls} to="/dashboard">{t("footer.links.dashboard")}</Link></li>
              <li><Link className={linkCls} to="/engagement">{t("footer.links.howItWorks")}</Link></li>
              <li><Link className={linkCls} to="/pricing">{t("footer.links.pricing")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("footer.company")}</h4>
            <ul className="mt-4 space-y-2.5">
              <li><Link className={linkCls} to="/about">{t("footer.links.whyDc")}</Link></li>
              <li><Link className={linkCls} to="/industries">{t("footer.links.industries")}</Link></li>
              <li><Link className={linkCls} to="/blog">{t("footer.links.insights")}</Link></li>
              <li><Link className={linkCls} to="/contact">{t("footer.links.contact")}</Link></li>
            </ul>
            <Link to="/contact" className="mt-5 inline-flex items-center text-sm font-semibold text-blue-700">
              {t("footer.submitInquiry")} <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-slate-200 pt-6 text-xs text-slate-500 md:flex-row md:items-center">
          <p>© {new Date().getFullYear()} Danny Clans Media Limited. {t("footer.rights")}</p>
          <nav aria-label="Legal" className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link className="hover:text-slate-800 hover:underline underline-offset-2" to="/terms-of-service" data-testid="footer-legal-terms-link">{t("footer.links.terms", "Terms of Service")}</Link>
            <Link className="hover:text-slate-800 hover:underline underline-offset-2" to="/privacy-policy"   data-testid="footer-legal-privacy-link">{t("footer.links.privacy", "Privacy Policy")}</Link>
            <Link className="hover:text-slate-800 hover:underline underline-offset-2" to="/refund-policy"    data-testid="footer-legal-refund-link">{t("footer.links.refund", "Refund & Withdrawal")}</Link>
            <Link className="hover:text-slate-800 hover:underline underline-offset-2" to="/kyc-aml-policy"   data-testid="footer-legal-kyc-link">{t("footer.links.kycAml", "KYC & AML")}</Link>
            <button
              type="button"
              onClick={() => { window.__openCookiePreferences?.(); }}
              className="cursor-pointer hover:text-slate-800 hover:underline underline-offset-2"
              data-testid="footer-cookie-preferences-button"
            >
              {t("footer.links.cookiePrefs", "Cookie preferences")}
            </button>
          </nav>
        </div>
      </div>
    </footer>
  );
};
